/**
 * Events API subscription rules (Slack's bot events; docs/features/integrations.md → Events API). An app subscribes to
 * event types in Settings → Apps; the server turns what happened in a workspace (an "occurrence") into the events each
 * installed app gets:
 *
 * - **Conversation events** (`message.*`, `app_mention`, `reaction_*`, `member_*_channel`) only for conversations the
 *   app's bot is in, as in Slack.
 * - **Workspace events** (`channel_created`, `channel_rename`) for every public channel, no membership needed.
 * - Each event needs its scope in the installation's granted scopes (`appEventScope`); subscribing needs the app's
 *   configured scopes to cover it (`missingEventScopes`).
 * - Edits and deletions are `message` events with Slack's `message_changed` / `message_deleted` subtypes, under the same
 *   `message.*` subscription and membership rules (never `app_mention`). A thread reply also sent to the channel is a
 *   `thread_broadcast` message.
 * - Payloads are Slack's (`slackMessageEvent` and friends): anything an app's bot posted carries `bot_id` (the bot user
 *   id, as `auth.test` answers) and `app_id`, so an app can skip its own posts.
 * - Loop guard: an app's events URL gets at most `SELF_EVENTS_PER_SECOND` events a second caused by its own bot; past
 *   that the app replies to itself in a loop, and deliveries stop for `EVENTS_LOOP_PAUSE_MS` (`selfEventCheck`).
 */
import type { App, AppEventType, AppScope, Block, ChannelKind, Inline, Message } from "@bundle/contracts";

/** Every scope, in the order the app editor lists them. */
export const APP_SCOPES: AppScope[] = [
  "chat:write", "channels:read", "channels:history", "users:read", "reactions:write", "commands",
  "groups:read", "groups:history", "im:history", "mpim:history", "app_mentions:read", "reactions:read",
];

/** Every bot event, in the order Event Subscriptions lists them. */
export const APP_EVENTS: AppEventType[] = [
  "message.channels", "message.groups", "message.im", "message.mpim", "app_mention", "reaction_added", "reaction_removed",
  "member_joined_channel", "member_left_channel", "channel_created", "channel_rename",
];

/** What happened, as the server sees it (before any app is considered). */
export type AppOccurrence =
  | { kind: "message"; conversation: ChannelKind }
  | { kind: "message_changed" | "message_deleted"; conversation: ChannelKind }
  | { kind: "reaction_added" | "reaction_removed"; conversation: ChannelKind }
  | { kind: "member_joined_channel" | "member_left_channel"; conversation: ChannelKind }
  | { kind: "channel_created" | "channel_rename"; conversation: ChannelKind };

/** One installed app, as far as the rules care: its subscriptions, its granted scopes and where its bot is. */
export type AppEventTarget = {
  events: readonly AppEventType[];
  scopes: readonly AppScope[];
  /** Its bot is a member of the occurrence's conversation. */
  inConversation: boolean;
  /** The message @-mentions its bot directly (not @here/@channel or a group). */
  mentioned?: boolean;
};

/** The `message.*` subscription for a conversation kind (Slack: channels, groups, im, mpim). */
export function messageEventType(conversation: ChannelKind): AppEventType {
  if (conversation === "private") return "message.groups";
  if (conversation === "dm") return "message.im";
  if (conversation === "group_dm") return "message.mpim";
  return "message.channels";
}

/** The scope an event needs. Membership events in private channels need `groups:read`, elsewhere `channels:read`. */
export function appEventScope(type: AppEventType, conversation?: ChannelKind): AppScope {
  switch (type) {
    case "message.channels":
      return "channels:history";
    case "message.groups":
      return "groups:history";
    case "message.im":
      return "im:history";
    case "message.mpim":
      return "mpim:history";
    case "app_mention":
      return "app_mentions:read";
    case "reaction_added":
    case "reaction_removed":
      return "reactions:read";
    case "member_joined_channel":
    case "member_left_channel":
      return conversation === "private" ? "groups:read" : "channels:read";
    case "channel_created":
    case "channel_rename":
      return "channels:read";
  }
}

/** Scopes the app must be configured for before it can subscribe to these events (each once, in event order). */
export function missingEventScopes(events: readonly AppEventType[], scopes: readonly AppScope[]): AppScope[] {
  const out: AppScope[] = [];
  for (const e of events) {
    const s = appEventScope(e);
    if (!scopes.includes(s) && !out.includes(s)) out.push(s);
  }
  return out;
}

/**
 * The events one app gets for an occurrence, in delivery order (a message that mentions the bot can be both
 * `message.channels` and `app_mention`, as in Slack). Empty = nothing for this app.
 */
export function appEventDeliveries(o: AppOccurrence, app: AppEventTarget): AppEventType[] {
  const wants = (t: AppEventType) => app.events.includes(t) && app.scopes.includes(appEventScope(t, o.conversation));
  const channel = o.conversation === "public" || o.conversation === "private";
  const out: AppEventType[] = [];
  switch (o.kind) {
    case "message": {
      if (!app.inConversation) return out;
      const t = messageEventType(o.conversation);
      if (wants(t)) out.push(t);
      // In a DM with the app every message is for it (message.im); app_mention is for conversations with others.
      if (app.mentioned === true && o.conversation !== "dm" && wants("app_mention")) out.push("app_mention");
      return out;
    }
    case "message_changed":
    case "message_deleted": {
      const t = messageEventType(o.conversation);
      if (app.inConversation && wants(t)) out.push(t);
      return out;
    }
    case "reaction_added":
    case "reaction_removed":
      if (app.inConversation && wants(o.kind)) out.push(o.kind);
      return out;
    case "member_joined_channel":
    case "member_left_channel":
      if (app.inConversation && channel && wants(o.kind)) out.push(o.kind);
      return out;
    case "channel_created":
    case "channel_rename":
      if (o.conversation === "public" && wants(o.kind)) out.push(o.kind);
      return out;
  }
}

/** Does any app's bot need to be told about this occurrence at all? (Workspace events: any bot; others: members.) */
export function occurrenceNeedsMembership(o: AppOccurrence): boolean {
  return o.kind !== "channel_created" && o.kind !== "channel_rename";
}

function inlineUsers(nodes: readonly Inline[], out: string[]): void {
  for (const n of nodes) if (n.type === "mention_user" && !out.includes(n.userId)) out.push(n.userId);
}

function blockUsers(blocks: readonly Block[], out: string[]): void {
  for (const b of blocks) {
    if (b.type === "paragraph") inlineUsers(b.children, out);
    else if (b.type === "quote") blockUsers(b.children, out);
    else if (b.type === "list") for (const item of b.items) blockUsers(item, out);
  }
}

/** People a message @-mentions by name (each once, in order), for `app_mention`. Broadcasts and groups don't count. */
export function directMentions(blocks: readonly Block[]): string[] {
  const out: string[] = [];
  blockUsers(blocks, out);
  return out;
}

// ---------- the event editor (Settings → Apps → Bot events) ----------

/** What the app editor picks: scopes and subscribed events. */
export type AppEventSelection = { scopes: AppScope[]; events: AppEventType[] };

/** Ticks / unticks a bot event. Ticking adds the scope it needs (as Slack's app settings do). Lists stay in editor order. */
export function toggleAppEvent(s: AppEventSelection, event: AppEventType): AppEventSelection {
  if (s.events.includes(event)) return { scopes: s.scopes, events: s.events.filter((x) => x !== event) };
  const need = appEventScope(event);
  return {
    scopes: APP_SCOPES.filter((x) => x === need || s.scopes.includes(x)),
    events: APP_EVENTS.filter((x) => x === event || s.events.includes(x)),
  };
}

/** Ticks / unticks a scope. Removing one drops the events that need it. */
export function toggleAppScope(s: AppEventSelection, scope: AppScope): AppEventSelection {
  if (s.scopes.includes(scope)) return { scopes: s.scopes.filter((x) => x !== scope), events: s.events.filter((e) => appEventScope(e) !== scope) };
  return { scopes: APP_SCOPES.filter((x) => x === scope || s.scopes.includes(x)), events: s.events };
}

// ---------- Slack payloads ----------

/** An installed app's bot user in this workspace. */
export type AppBot = { userId: string; appId: string };

const CHANNEL_TYPE: Record<ChannelKind, string> = { public: "channel", private: "group", dm: "im", group_dm: "mpim" };

/** Slack's `channel_type` for a conversation kind. */
export const slackChannelType = (kind: ChannelKind): string => CHANNEL_TYPE[kind] ?? "channel";

/**
 * The app bot behind a message: its bot wrote it (chat.postMessage), or it's an app's answer posted for someone (a
 * slash command's in_channel reply, shown as the app). Null for people, incoming webhooks and built-ins.
 */
export function messageBot(m: Message, bots: readonly AppBot[]): AppBot | null {
  const author = bots.find((b) => b.userId === m.authorId);
  if (author) return author;
  const app = m.app;
  if (app === undefined) return null;
  return bots.find((b) => b.appId === app.appId) ?? null;
}

/**
 * Slack's message object (the event body, or `message` / `previous_message` / `root`). `ts` is the message id, as in the
 * Web API. An app bot's posts carry `bot_id` (its user id) and `app_id`; incoming webhooks and other integrations are
 * Slack's `bot_message` (`bot_id` = the integration, `username`); a thread reply also sent to the channel is
 * `thread_broadcast`.
 */
export function slackMessage(m: Message, bots: readonly AppBot[]): Record<string, unknown> {
  const out: Record<string, unknown> = { type: "message", user: m.authorId, text: m.text, ts: m.id };
  if (m.threadRootId !== undefined) {
    out.thread_ts = m.threadRootId;
    if (m.alsoSentToChannel === true) out.subtype = "thread_broadcast";
  }
  const bot = messageBot(m, bots);
  if (bot !== null) {
    out.bot_id = bot.userId;
    out.app_id = bot.appId;
  } else if (m.app !== undefined) {
    if (out.subtype === undefined) out.subtype = "bot_message";
    out.bot_id = m.app.appId;
    out.username = m.app.name;
  }
  if (m.appBlocks !== undefined) out.blocks = m.appBlocks;
  if (m.files.length) out.files = m.files.map((f) => ({ id: f.id, name: f.name, mimetype: f.mime, size: f.size }));
  if (m.clientId !== undefined) out.client_msg_id = m.clientId;
  if (m.editedAt !== undefined) out.edited = { user: m.authorId, ts: (m.editedAt / 1000).toFixed(6) };
  return out;
}

/** A new message (`message.*`). A `thread_broadcast` carries its thread's `root` when it's known. */
export function slackMessageEvent(m: Message, kind: ChannelKind, bots: readonly AppBot[], root?: Message): Record<string, unknown> {
  const out: Record<string, unknown> = { ...slackMessage(m, bots), channel: m.channelId, channel_type: slackChannelType(kind) };
  if (out.subtype === "thread_broadcast" && root !== undefined) out.root = slackMessage(root, bots);
  return out;
}

/** The bot fields an edit or deletion of a bot's message carries at the top level too (so one filter fits all). */
function botFields(m: Message, bots: readonly AppBot[]): Record<string, unknown> {
  const bot = messageBot(m, bots);
  return bot === null ? {} : { bot_id: bot.userId, app_id: bot.appId };
}

/** Slack's `message_changed`: `message` is the new version, `previous_message` the old; `ts` / `event_ts` = when. */
export function slackMessageChangedEvent(prev: Message, next: Message, kind: ChannelKind, bots: readonly AppBot[], eventTs: string): Record<string, unknown> {
  return {
    type: "message", subtype: "message_changed", hidden: true, channel: next.channelId, channel_type: slackChannelType(kind), ts: eventTs, event_ts: eventTs,
    message: slackMessage(next, bots), previous_message: slackMessage(prev, bots), ...botFields(next, bots),
  };
}

/** Slack's `message_deleted`: `deleted_ts` is the message, `previous_message` what it said. */
export function slackMessageDeletedEvent(prev: Message, kind: ChannelKind, bots: readonly AppBot[], eventTs: string): Record<string, unknown> {
  return {
    type: "message", subtype: "message_deleted", hidden: true, channel: prev.channelId, channel_type: slackChannelType(kind), ts: eventTs, event_ts: eventTs,
    deleted_ts: prev.id, previous_message: slackMessage(prev, bots), ...botFields(prev, bots),
  };
}

// ---------- loop guard ----------

/** Events a second an app's events URL may get that its own bot caused. */
export const SELF_EVENTS_PER_SECOND = 10;
/** How long deliveries stop once an app answers itself in a loop. */
export const EVENTS_LOOP_PAUSE_MS = 60_000;
/** Settings → Apps shows the loop warning this long after the last pause. */
export const EVENTS_LOOP_WARNING_MS = 24 * 60 * 60 * 1000;

/** Did the app's own bot cause this event (its message, edit or deletion, its reaction, its mention of itself)? */
export function causedByBot(event: Record<string, unknown>, botUserId: string): boolean {
  return event.bot_id === botUserId || event.user === botUserId;
}

/**
 * One more own-bot event for an app at `now`, given the times (ms) of the ones it was sent before. `allowed` false = the
 * last second already had `SELF_EVENTS_PER_SECOND`: drop it and pause the app (a loop). `recent` = the window to keep.
 */
export function selfEventCheck(recent: readonly number[], now: number): { allowed: boolean; recent: number[] } {
  const window = recent.filter((t) => t > now - 1000 && t <= now);
  if (window.length >= SELF_EVENTS_PER_SECOND) return { allowed: false, recent: window };
  return { allowed: true, recent: [...window, now] };
}

/** Deliveries to the app are paused right now (the loop guard tripped less than a minute ago). */
export function eventsPaused(app: Pick<App, "eventsLoopAt">, now: number): boolean {
  return app.eventsLoopAt !== undefined && now >= app.eventsLoopAt && now < app.eventsLoopAt + EVENTS_LOOP_PAUSE_MS;
}

/** Settings → Apps warns about a reply loop while deliveries are paused and for a day after. */
export function showsEventsLoopWarning(app: Pick<App, "eventsLoopAt">, now: number): boolean {
  return app.eventsLoopAt !== undefined && now < app.eventsLoopAt + EVENTS_LOOP_WARNING_MS;
}
